import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  cloudAlertAction,
  cloudAdvanceTicket,
  cloudAddTicketEvent,
  cloudDeleteRule,
  cloudHealth,
  cloudListAlerts,
  cloudListRules,
  cloudListTickets,
  ensureCloudSession,
  cloudToggleRule,
  cloudUpsertRule,
  getCloudToken,
  type ApiAlert,
  type ApiRule,
  type ApiTicket,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import type { AlertItem, AlertRule, Ticket, TicketEvent } from "./mock";

function nowLabel(): string {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `今天 ${hh}:${mm}`;
}

const TICKET_FLOW: Ticket["status"][] = [
  "待分派",
  "处理中",
  "待回访",
  "已关闭",
];

function mapAlert(a: ApiAlert): AlertItem {
  return {
    id: a.id,
    projectId: a.projectId,
    projectName: a.projectName,
    level: a.level,
    rule: a.rule,
    target: a.target,
    status: a.status,
    createdAt: a.createdAt,
    notified: a.notified,
    ticketId: a.ticketId || undefined,
    signature: a.signature || undefined,
  };
}

function mapRule(r: ApiRule): AlertRule {
  return {
    id: r.id,
    name: r.name,
    level: r.level,
    trigger: r.trigger,
    channels: r.channels,
    quietMinutes: r.quietMinutes,
    enabled: r.enabled,
    autoTicket: r.autoTicket,
  };
}

function mapTicket(t: ApiTicket): Ticket {
  return {
    id: t.id,
    projectId: t.projectId,
    projectName: t.projectName,
    source: t.source,
    title: t.title,
    priority: t.priority,
    status: t.status,
    assignee: t.assignee,
    createdAt: t.createdAt,
    device: t.device,
    needVisit: Boolean(t.needVisit),
    alertId: t.alertId,
    signature: t.signature || undefined,
    alertCount: t.alertCount ?? 0,
    openAlertCount: t.openAlertCount ?? 0,
    diagnosis: t.diagnosis,
    serviceNote: t.serviceNote,
    timeline: t.timeline,
  };
}

interface OpsState {
  alerts: AlertItem[];
  tickets: Ticket[];
  rules: AlertRule[];
  cloudReady: boolean;
  addAlert: (alert: AlertItem) => void;
  updateAlert: (alert: AlertItem) => void;
  silenceAlert: (id: string) => void;
  recoverAlert: (id: string) => void;
  convertAlertToTicket: (alertId: string) => Promise<string | undefined>;
  addTicket: (ticket: Ticket) => void;
  updateTicket: (ticket: Ticket) => void;
  advanceTicket: (id: string, nextStatus?: Ticket["status"]) => void;
  addTicketEvent: (id: string, event: TicketEvent) => void;
  upsertRule: (rule: AlertRule) => void;
  removeRule: (id: string) => void;
  toggleRule: (id: string) => void;
  refreshOps: () => Promise<void>;
}

const OpsContext = createContext<OpsState | null>(null);

export function OpsProvider({ children }: { children: ReactNode }) {
  const { email, isAuthenticated } = useAuth();
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [cloudReady, setCloudReady] = useState(false);

  const refreshOps = useCallback(async () => {
    try {
      const ok = await cloudHealth();
      if (!ok) {
        setCloudReady(false);
        return;
      }
      if (!getCloudToken() && email) {
        try {
          const sess = await ensureCloudSession(email);
          if (sess.ok === false) throw new Error(sess.message);
        } catch {
          setCloudReady(false);
          return;
        }
      }
      if (!getCloudToken()) {
        setCloudReady(false);
        return;
      }
      const [a, r, t] = await Promise.all([
        cloudListAlerts(),
        cloudListRules(),
        cloudListTickets(),
      ]);
      // Prefer cloud data when connected (SaaS source of truth)
      setAlerts(a.items.map(mapAlert));
      setRules(r.items.map(mapRule));
      setTickets(t.items.map(mapTicket));
      setCloudReady(true);
    } catch {
      setCloudReady(false);
    }
  }, [email]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void refreshOps();
    const timer = window.setInterval(() => void refreshOps(), 12000);
    return () => window.clearInterval(timer);
  }, [isAuthenticated, refreshOps]);

  const value = useMemo<OpsState>(
    () => ({
      alerts,
      tickets,
      rules,
      cloudReady,
      refreshOps,
      addAlert: (alert) => setAlerts((prev) => [alert, ...prev]),
      updateAlert: (alert) =>
        setAlerts((prev) =>
          prev.map((a) => (a.id === alert.id ? alert : a)),
        ),
      silenceAlert: (id) => {
        setAlerts((prev) =>
          prev.map((a) =>
            a.id === id ? { ...a, status: "已静默" } : a,
          ),
        );
        if (cloudReady) {
          void cloudAlertAction(id, "silence").catch(() => undefined);
        }
      },
      recoverAlert: (id) => {
        setAlerts((prev) =>
          prev.map((a) =>
            a.id === id ? { ...a, status: "已恢复" } : a,
          ),
        );
        if (cloudReady) {
          void cloudAlertAction(id, "recover").catch(() => undefined);
        }
      },
      convertAlertToTicket: async (alertId) => {
        if (!cloudReady) return undefined;
        try {
          const res = await cloudAlertAction(alertId, "to-ticket");
          if (res.ticket) {
            setTickets((prev) => {
              const mapped = mapTicket(res.ticket!);
              const idx = prev.findIndex((t) => t.id === mapped.id);
              if (idx >= 0) {
                const next = [...prev];
                next[idx] = mapped;
                return next;
              }
              return [mapped, ...prev];
            });
          }
          if (res.alert) {
            setAlerts((prev) =>
              prev.map((a) =>
                a.id === alertId ? mapAlert(res.alert!) : a,
              ),
            );
          }
          void refreshOps();
          return res.ticket?.id;
        } catch {
          return undefined;
        }
      },
      addTicket: (ticket) => setTickets((prev) => [ticket, ...prev]),
      updateTicket: (ticket) =>
        setTickets((prev) =>
          prev.map((t) => (t.id === ticket.id ? ticket : t)),
        ),
      advanceTicket: (id, nextStatus) => {
        let closed = false;
        setTickets((prev) =>
          prev.map((t) => {
            if (t.id !== id) return t;
            const idx = TICKET_FLOW.indexOf(t.status);
            const status =
              nextStatus ??
              (idx >= 0
                ? TICKET_FLOW[Math.min(idx + 1, TICKET_FLOW.length - 1)]!
                : t.status);
            if (status === t.status) return t;
            closed = status === "已关闭" || status === "已取消";
            const event: TicketEvent = {
              at: nowLabel(),
              actor: "系统",
              text: closed
                ? `状态变更为「${status}」· 集合内告警实例已标记为已处理`
                : `状态变更为「${status}」`,
            };
            return {
              ...t,
              status,
              timeline: [...(t.timeline ?? []), event],
            };
          }),
        );
        if (closed) {
          setAlerts((prev) =>
            prev.map((a) =>
              a.ticketId === id && a.status !== "已处理"
                ? { ...a, status: "已处理" }
                : a,
            ),
          );
        }
        if (cloudReady) {
          void cloudAdvanceTicket(id, nextStatus)
            .then((res) => {
              if (res.ticket) {
                setTickets((prev) =>
                  prev.map((t) =>
                    t.id === id ? mapTicket(res.ticket) : t,
                  ),
                );
              }
              void refreshOps();
            })
            .catch(() => undefined);
        }
      },
      addTicketEvent: (id, event) => {
        setTickets((prev) =>
          prev.map((t) =>
            t.id === id
              ? { ...t, timeline: [...(t.timeline ?? []), event] }
              : t,
          ),
        );
        if (cloudReady) {
          void cloudAddTicketEvent(id, event.text)
            .then((res) => {
              if (res.ticket) {
                setTickets((prev) =>
                  prev.map((t) =>
                    t.id === id ? mapTicket(res.ticket) : t,
                  ),
                );
              }
            })
            .catch(() => undefined);
        }
      },
      upsertRule: (rule) => {
        setRules((prev) => {
          const idx = prev.findIndex((r) => r.id === rule.id);
          if (idx === -1) return [rule, ...prev];
          const next = [...prev];
          next[idx] = rule;
          return next;
        });
        if (cloudReady) {
          void cloudUpsertRule(rule).catch(() => undefined);
        }
      },
      removeRule: (id) => {
        setRules((prev) => prev.filter((r) => r.id !== id));
        if (cloudReady) {
          void cloudDeleteRule(id).catch(() => undefined);
        }
      },
      toggleRule: (id) => {
        setRules((prev) =>
          prev.map((r) =>
            r.id === id ? { ...r, enabled: !r.enabled } : r,
          ),
        );
        if (cloudReady) {
          void cloudToggleRule(id).catch(() => undefined);
        }
      },
    }),
    [alerts, tickets, rules, cloudReady, refreshOps],
  );

  return <OpsContext.Provider value={value}>{children}</OpsContext.Provider>;
}

export function useOps() {
  const ctx = useContext(OpsContext);
  if (!ctx) {
    throw new Error("useOps must be used within OpsProvider");
  }
  return ctx;
}
